import React from "react";
import { Target, Eye, Heart, ArrowUpRight } from "lucide-react";
import { waLink } from "../lib/whatsapp";
import { useSiteSettings } from "../lib/siteSettings";
import PageHero from "../components/PageHero";

const VALORES = [
  "Foco total no cliente",
  "Compromisso com resultados",
  "Qualidade e confiabilidade",
  "Agilidade no atendimento",
  "Desenvolvimento e valorização de pessoas",
  "Parcerias de longo prazo",
  "Ética e transparência",
];

const ABOUT_IMG = "https://static.prod-images.emergentagent.com/jobs/1b35fc0a-0293-4ff8-ac55-1a277592b1f0/images/9251f1703331ea0d61a840d98f27f1c315e21c4c66733d00c669c777b262c0e1.png";

export default function Sobre() {
  const { settings } = useSiteSettings();
  return (
    <main data-testid="sobre-page" className="bg-white">
      <PageHero
        overline="QUEM SOMOS"
        title="Somos a Avantech. Parceiro de crescimento."
        image={settings.cover_sobre}
      />

      {/* HISTÓRIA */}
      <section className="py-20 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 grid md:grid-cols-12 gap-12 items-start">
          <div className="md:col-span-5">
            <img src={ABOUT_IMG} alt="Avantech" className="w-full object-cover" />
            <div className="bg-[#1A1A1A] text-white p-8 mt-4">
              <div className="font-mono-sm text-[11px] tracking-[0.25em] uppercase text-[#E84E34]">
                Desde 12/2022
              </div>
              <div className="font-display text-3xl font-black mt-2">3+ anos transformando operações</div>
            </div>
          </div>
          <div className="md:col-span-7">
            <div className="font-mono-sm text-xs tracking-[0.3em] uppercase text-[#E84E34] mb-4">
              // NOSSA HISTÓRIA
            </div>
            <h2 className="font-display text-3xl md:text-4xl font-black tracking-tight leading-[1.05]">
              Mais do que locação. Parceria estratégica.
            </h2>
            <div className="mt-6 space-y-5 text-base text-[#374151] leading-relaxed">
              <p>
                A Avantech nasceu em <strong>dezembro de 2022</strong> a partir de uma necessidade clara
                do mercado: empresas não buscavam apenas a locação de máquinas, mas sim um <strong>parceiro
                estratégico</strong>, que transformasse a gestão de impressão e automação — simplificando processos,
                reduzindo custos e aumentando performance.
              </p>
              <p>
                Com esse propósito, iniciamos nossa jornada com a missão de transformar a forma como
                as empresas gerenciam seus parques de impressão e automação. Desde o início incorporamos
                a <strong>fabricação de etiquetas para impressoras térmicas sob demanda</strong>, alinhada
                às necessidades específicas de cada cliente, junto ao serviço de Outsourcing de impressão.
              </p>
              <p>
                Somos uma empresa especializada em <strong>soluções inteligentes de Outsourcing de impressão
                e automação</strong>, com foco em eficiência, economia e personalização. Contamos com uma
                equipe preparada e em constante evolução, pronta para entregar as melhores soluções em
                equipamentos e insumos — impressoras laser, jato de tinta, térmicas e ribbons.
              </p>
              <p className="text-[#1A1A1A] font-semibold border-l-2 border-[#E84E34] pl-4">
                Avantech não é fornecedor. É parceiro de crescimento.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* PROPÓSITO / VISÃO */}
      <section className="bg-[#F9F9F9] py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 grid md:grid-cols-2 gap-px bg-[#E5E7EB] border border-[#E5E7EB]">
          <div className="bg-white p-8 md:p-12">
            <Target size={32} strokeWidth={1.5} className="text-[#E84E34]" />
            <div className="font-mono-sm text-[10px] tracking-[0.25em] uppercase text-[#6B7280] mt-6">
              Propósito
            </div>
            <h3 className="font-display text-2xl md:text-3xl font-black mt-2 leading-tight">
              Soluções completas que geram resultado.
            </h3>
            <p className="mt-4 text-[#374151] leading-relaxed">
              Oferecer soluções completas em automação, outsourcing de impressão e etiquetas,
              com foco em redução de custos, ganho de produtividade e eficiência operacional para
              nossos clientes.
            </p>
          </div>
          <div className="bg-white p-8 md:p-12">
            <Eye size={32} strokeWidth={1.5} className="text-[#E84E34]" />
            <div className="font-mono-sm text-[10px] tracking-[0.25em] uppercase text-[#6B7280] mt-6">
              Visão
            </div>
            <h3 className="font-display text-2xl md:text-3xl font-black mt-2 leading-tight">
              Referência em qualidade e eficiência.
            </h3>
            <p className="mt-4 text-[#374151] leading-relaxed">
              Ser reconhecida no nosso mercado de atuação como referência de qualidade e eficiência.
            </p>
          </div>
        </div>
      </section>

      {/* VALORES */}
      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16">
          <div className="grid md:grid-cols-12 gap-8 mb-12">
            <div className="md:col-span-5">
              <Heart size={28} strokeWidth={1.5} className="text-[#E84E34]" />
              <div className="font-mono-sm text-xs tracking-[0.3em] uppercase text-[#E84E34] mt-4 mb-3">
                // PRINCÍPIOS E VALORES
              </div>
              <h2 className="font-display text-3xl md:text-5xl font-black tracking-tight leading-[1.05]">
                O que nos move todos os dias.
              </h2>
            </div>
            <div className="md:col-span-7">
              <ul className="divide-y divide-[#E5E7EB] border-y border-[#E5E7EB]">
                {VALORES.map((v, i) => (
                  <li key={i} className="flex items-center gap-6 py-5">
                    <span className="font-mono-sm text-[#E84E34] text-sm w-10">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <span className="font-display text-lg md:text-xl font-bold">{v}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#0F0F10] text-white py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div>
            <h3 className="font-display text-2xl md:text-3xl font-black">
              Vamos transformar sua operação juntos?
            </h3>
            <p className="mt-2 text-white/70">Fale com a nossa equipe agora mesmo.</p>
          </div>
          <a
            href={waLink("Olá Avantech! Li sobre vocês no site e quero conversar.")}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="sobre-cta-whatsapp"
            className="inline-flex items-center gap-3 bg-[#E84E34] text-white font-bold px-7 py-4 uppercase text-xs tracking-[0.2em] hover:bg-[#CF412A] transition-colors"
          >
            Falar no WhatsApp <ArrowUpRight size={18} />
          </a>
        </div>
      </section>
    </main>
  );
}
